import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography, Container, Paper, Button, Box, Grid, TextField,
  Divider, CircularProgress, Alert, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell,
  ToggleButton, ToggleButtonGroup, Autocomplete, Chip,
} from '@mui/material';
import {
  Add, Delete, Print, Receipt, Money, CreditCard,
  AccountBalanceWallet, CheckCircle, Refresh,
} from '@mui/icons-material';
import {
  ipdService, billingService, serviceRateService,
  printSettingsService, authService,
} from '../../services/api';
import TopBar from '../../components/TopBar';

const newRow = () => ({ _id: Date.now() + Math.random(), service: '', qty: 1, rate: '', amount: 0 });

const PAYMENT_MODES = [
  { value: 'Cash', label: 'Cash',  icon: <Money fontSize="small" /> },
  { value: 'Card', label: 'Card',  icon: <CreditCard fontSize="small" /> },
  { value: 'UPI',  label: 'UPI',   icon: <AccountBalanceWallet fontSize="small" /> },
];

const calcAge = (dob) => {
  if (!dob) return '';
  const today = new Date(); const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  if (today.getMonth() - birth.getMonth() < 0 ||
      (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
  return age;
};

const fmtDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const stayDays = (admDate, disDate) => {
  if (!admDate) return 0;
  const from = new Date(admDate);
  const to = disDate ? new Date(disDate) : new Date();
  return Math.max(1, Math.round((to - from) / (1000 * 60 * 60 * 24)));
};

const IpdBillingPage = () => {
  const { admissionId } = useParams();
  const navigate = useNavigate();
  const user = authService.getCurrentUser();

  const [admission, setAdmission] = useState(null);
  const [serviceRates, setServiceRates] = useState([]);
  const [printSettings, setPrintSettings] = useState(null);
  const [existingBills, setExistingBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [billId, setBillId] = useState(null);
  const [shouldPrint, setShouldPrint] = useState(false);
  const [error, setError] = useState('');

  const [rows, setRows] = useState([newRow()]);
  const [discount, setDiscount] = useState(0);
  const [cgst, setCgst] = useState(0);
  const [sgst, setSgst] = useState(0);
  const [paymentMode, setPaymentMode] = useState('Cash');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [admRes, srRes, psRes, billsRes] = await Promise.all([
        ipdService.getById(admissionId),
        serviceRateService.getAll(),
        printSettingsService.getSettings(),
        billingService.getBillsByAdmission(admissionId),
      ]);
      const adm = admRes.data;
      setAdmission(adm);
      setServiceRates(srRes.data || []);
      setPrintSettings(psRes.data);
      setExistingBills(billsRes.data || []);

      // Auto-populate bed charges if no existing bill
      if (!(billsRes.data?.length)) {
        const findBedRate = (bedType) => (srRes.data || []).find(r =>
          r.serviceName.toLowerCase().includes('bed') ||
          r.serviceName.toLowerCase().includes('room') ||
          (bedType && r.serviceName.toLowerCase().includes(bedType.toLowerCase()))
        );

        let transfers = [];
        try {
          const tRes = await ipdService.getBedTransfers(admissionId);
          transfers = (tRes.data || []).slice().sort((a, b) => new Date(a.transferredAt) - new Date(b.transferredAt));
        } catch { /* no transfer history available */ }

        if (transfers.length === 0) {
          const days = stayDays(adm.admissionDate, adm.actualDischargeDate);
          const bedRate = findBedRate(adm.bed?.bedType || '');
          const row = newRow();
          row.service = `Bed / Room Charges (${adm.ward?.name || 'Ward'})`;
          row.qty     = days;
          row.rate    = bedRate ? String(bedRate.rate) : '';
          row.amount  = bedRate ? days * bedRate.rate : 0;
          setRows([row]);
        } else {
          // Patient occupied more than one room during this stay (transferred mid-admission) —
          // bill one row per room segment instead of charging the whole stay at the current rate.
          const msPerDay = 1000 * 60 * 60 * 24;
          const segments = [];
          let segStart = new Date(adm.admissionDate);
          let segWard = transfers[0].fromWard;
          let segBedType = transfers[0].fromBedType;
          transfers.forEach((t) => {
            segments.push({ start: segStart, end: new Date(t.transferredAt), ward: segWard, bedType: segBedType });
            segStart = new Date(t.transferredAt);
            segWard = t.toWard;
            segBedType = t.toBedType;
          });
          const dischargeOrNow = adm.actualDischargeDate ? new Date(adm.actualDischargeDate) : new Date();
          segments.push({ start: segStart, end: dischargeOrNow, ward: segWard, bedType: segBedType });

          // Last segment absorbs the rounding so the sum always matches the admission's total stay days.
          const totalDays = stayDays(adm.admissionDate, adm.actualDischargeDate);
          let allocated = 0;
          const autoRows = segments.map((seg, idx) => {
            const days = idx === segments.length - 1
              ? Math.max(0, totalDays - allocated)
              : Math.max(0, Math.round((seg.end - seg.start) / msPerDay));
            if (idx !== segments.length - 1) allocated += days;

            const rate = findBedRate(seg.bedType);
            const row = newRow();
            row.service = `Bed / Room Charges (${seg.ward || 'Ward'}${seg.bedType ? ' - ' + seg.bedType : ''})`;
            row.qty     = days;
            row.rate    = rate ? String(rate.rate) : '';
            row.amount  = rate ? days * rate.rate : 0;
            return row;
          }).filter(r => r.qty > 0);

          setRows(autoRows.length ? autoRows : [newRow()]);
        }
      }
    } catch {
      setError('Failed to load billing data.');
    } finally {
      setLoading(false);
    }
  }, [admissionId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (shouldPrint) { window.print(); setShouldPrint(false); }
  }, [shouldPrint]);

  const updateRow = (id, field, value) => {
    setRows(prev => prev.map(r => {
      if (r._id !== id) return r;
      const updated = { ...r, [field]: value };
      if (field === 'qty' || field === 'rate') {
        const qty  = parseFloat(field === 'qty'  ? value : r.qty)  || 0;
        const rate = parseFloat(field === 'rate' ? value : r.rate) || 0;
        updated.amount = qty * rate;
      }
      return updated;
    }));
  };

  const selectService = (rowId, sr) => {
    setRows(prev => prev.map(r => {
      if (r._id !== rowId) return r;
      if (!sr) return { ...r, service: '', qty: 1, rate: '', amount: 0 };
      return { ...r, service: sr.serviceName, qty: 1, rate: String(sr.rate), amount: sr.rate };
    }));
  };

  const addRow    = () => setRows(prev => [...prev, newRow()]);
  const deleteRow = (id) => setRows(prev => prev.filter(r => r._id !== id));

  const subtotal    = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const discountAmt = (subtotal * (parseFloat(discount) || 0)) / 100;
  const afterDisc   = subtotal - discountAmt;
  const cgstAmt     = (afterDisc * (parseFloat(cgst) || 0)) / 100;
  const sgstAmt     = (afterDisc * (parseFloat(sgst) || 0)) / 100;
  const netAmount   = afterDisc + cgstAmt + sgstAmt;
  const fmt         = (n) => Number(n).toFixed(2);

  const handleSaveAndPrint = async () => {
    if (!admission) return;
    setSaving(true);
    setSaveStatus('saving');
    setError('');
    try {
      const res = await billingService.createBill({
        admissionId:    parseInt(admissionId),
        patientId:      admission.patientId,
        billedByUserId: user?.id,
        billDate:       new Date().toISOString(),
        paymentMode,
        subtotal,
        discountPct:    parseFloat(discount) || 0,
        discountAmount: discountAmt,
        cgstPct:        parseFloat(cgst) || 0,
        cgstAmount:     cgstAmt,
        sgstPct:        parseFloat(sgst) || 0,
        sgstAmount:     sgstAmt,
        netAmount,
        items: rows.map(r => ({
          serviceName: r.service || '—',
          qty:    parseFloat(r.qty)  || 1,
          rate:   parseFloat(r.rate) || 0,
          amount: r.amount || 0,
        })),
      });
      setBillId(res.data?.id || null);
      setSaveStatus('saved');
      await load();
      setShouldPrint(true);
    } catch {
      setSaveStatus('error');
      setError('Failed to save bill.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const patient = admission?.patient;
  const patientAge = calcAge(patient?.dateOfBirth);
  const days = stayDays(admission?.admissionDate, admission?.actualDischargeDate);

  const isPrePrinted = (printSettings?.letterheadType || printSettings?.LetterheadType || 'Pre-Printed Letterhead') === 'Pre-Printed Letterhead';
  const mt = printSettings?.marginTop    ?? printSettings?.MarginTop    ?? 10;
  const mb = printSettings?.marginBottom ?? printSettings?.MarginBottom ?? 10;
  const ml = printSettings?.marginLeft   ?? printSettings?.MarginLeft   ?? 10;
  const mr = printSettings?.marginRight  ?? printSettings?.MarginRight  ?? 10;
  const hospitalName  = printSettings?.hospitalName  || printSettings?.HospitalName  || '';
  const hospitalAddr  = printSettings?.address       || printSettings?.Address       || '';
  const hospitalPhone = printSettings?.phoneNumber   || printSettings?.PhoneNumber   || '';
  const logoUrl       = printSettings?.logoUrl       || printSettings?.LogoUrl       || null;

  return (
    <>
      <style>{`
        @media screen { #ipd-bill-print { display: none !important; } }
        @media print {
          @page { margin: ${mt}mm ${mr}mm ${mb}mm ${ml}mm; }
          #ipd-bill-screen { display: none !important; }
          #ipd-bill-print {
            display: block !important;
            position: absolute; left: 0; top: 0;
            width: 100%; padding: 0;
            box-sizing: border-box;
            font-family: Arial, sans-serif;
            font-size: 12px;
          }
          #ipd-bill-print * { visibility: visible !important; }
          .pb-table { width: 100%; border-collapse: collapse; }
          .pb-table td, .pb-table th { border: 1px solid #1565c0; padding: 5px 8px; }
          .pb-label { color: #1565c0; font-weight: 600; }
          .pb-total-row td { background-color: #e3f2fd !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; font-weight: 600; }
          .pb-net-row td { background-color: #1565c0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact;
            color: #fff !important; font-weight: 700; font-size: 14px; }
          .pb-hosp-header { text-align: center; margin-bottom: 8px; }
        }
      `}</style>

      {/* ── Screen ─────────────────────────────────────────────────────── */}
      <div id="ipd-bill-screen" className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <TopBar
          title="IPD Billing"
          onBack={() => navigate(-1)}
          noPrint
          actions={
            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={handleSaveAndPrint}
              disabled={saving}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
            >
              {saving ? 'Saving…' : 'Save & Print'}
            </Button>
          }
        />

        <Container maxWidth="lg" sx={{ py: 4 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {saveStatus === 'saved' && (
            <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
              Bill #{billId} saved successfully.
            </Alert>
          )}

          {/* Existing Bills */}
          {existingBills.length > 0 && (
            <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, color: '#e65100' }}>
                Existing Bills for This Admission
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fff3e0' }}>
                    <TableCell><strong>Bill #</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell align="right"><strong>Net Amount</strong></TableCell>
                    <TableCell><strong>Payment Mode</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {existingBills.map(b => (
                    <TableRow key={b.id}>
                      <TableCell>{b.id}</TableCell>
                      <TableCell>{fmtDate(b.billDate)}</TableCell>
                      <TableCell align="right">₹{fmt(b.netAmount)}</TableCell>
                      <TableCell>{b.paymentMode}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Generating a new bill will be added to the above list.
              </Typography>
            </Paper>
          )}

          {/* Patient Info */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Receipt sx={{ fontSize: 28, color: '#1565c0', mr: 1.5 }} />
              <Typography variant="h6" fontWeight={600}>Patient &amp; Admission Details</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Patient Name"
                  value={patient ? `${patient.firstName} ${patient.lastName}` : '—'}
                  InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth label="Age / Gender"
                  value={`${patientAge || '—'} / ${patient?.gender || '—'}`}
                  InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth label="Admission #"
                  value={admissionId}
                  InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Ward / Bed"
                  value={`${admission?.ward?.name || '—'} / ${admission?.bed?.bedNumber || '—'}`}
                  InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Admission Date"
                  value={fmtDate(admission?.admissionDate)}
                  InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Chip label={`${days} day${days > 1 ? 's' : ''} stay`} color="primary" variant="outlined" />
                  <Chip label={admission?.status || '—'}
                    color={admission?.status === 'Discharged' ? 'success' : 'warning'}
                    variant="outlined" />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Line Items */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>Bill Items</Typography>
              <Button variant="outlined" size="small" startIcon={<Add />} onClick={addRow}>
                Add Row
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell width="50%"><strong>Service / Item</strong></TableCell>
                  <TableCell width="10%" align="right"><strong>Qty</strong></TableCell>
                  <TableCell width="17%" align="right"><strong>Rate (₹)</strong></TableCell>
                  <TableCell width="18%" align="right"><strong>Amount (₹)</strong></TableCell>
                  <TableCell width="5%" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row._id}>
                    <TableCell>
                      <Autocomplete
                        freeSolo
                        fullWidth
                        size="small"
                        options={serviceRates}
                        getOptionLabel={o => (typeof o === 'string' ? o : o.serviceName)}
                        inputValue={row.service}
                        onInputChange={(_, val) => updateRow(row._id, 'service', val)}
                        onChange={(_, val) => {
                          if (val && typeof val !== 'string') selectService(row._id, val);
                        }}
                        renderInput={(params) => (
                          <TextField {...params} variant="standard" placeholder="Type or select service" />
                        )}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        variant="standard" type="number" size="small"
                        inputProps={{ min: 0, style: { textAlign: 'right' } }}
                        value={row.qty}
                        onChange={e => updateRow(row._id, 'qty', e.target.value)}
                        sx={{ width: 70 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        variant="standard" type="number" size="small"
                        inputProps={{ min: 0, style: { textAlign: 'right' } }}
                        value={row.rate}
                        onChange={e => updateRow(row._id, 'rate', e.target.value)}
                        sx={{ width: 90 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={500}>
                        {fmt(row.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Remove row">
                        <IconButton size="small" color="error" onClick={() => deleteRow(row._id)}
                          disabled={rows.length === 1}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          {/* Totals + Payment */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={7}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Payment</Typography>
                <ToggleButtonGroup
                  exclusive value={paymentMode} onChange={(_, v) => v && setPaymentMode(v)}
                  size="small" sx={{ mb: 2 }}
                >
                  {PAYMENT_MODES.map(m => (
                    <ToggleButton key={m.value} value={m.value} sx={{ gap: 0.5 }}>
                      {m.icon} {m.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField fullWidth size="small" label="Discount (%)"
                      type="number" inputProps={{ min: 0, max: 100 }}
                      value={discount} onChange={e => setDiscount(e.target.value)} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth size="small" label="CGST (%)"
                      type="number" inputProps={{ min: 0 }}
                      value={cgst} onChange={e => setCgst(e.target.value)} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth size="small" label="SGST (%)"
                      type="number" inputProps={{ min: 0 }}
                      value={sgst} onChange={e => setSgst(e.target.value)} />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={5}>
                <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                  {[
                    { label: 'Subtotal', value: subtotal },
                    { label: `Discount (${discount || 0}%)`, value: -discountAmt },
                    { label: `CGST (${cgst || 0}%)`, value: cgstAmt },
                    { label: `SGST (${sgst || 0}%)`, value: sgstAmt },
                  ].map(({ label, value }) => (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">{label}</Typography>
                      <Typography variant="body2">₹{fmt(Math.abs(value))}</Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={700} color="primary">NET TOTAL</Typography>
                    <Typography variant="h6" fontWeight={700} color="primary">₹{fmt(netAmount)}</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </div>

      {/* ── Print View ──────────────────────────────────────────────────── */}
      <div id="ipd-bill-print">
        {/* Hospital Header */}
        {!isPrePrinted && (
          <div className="pb-hosp-header">
            {logoUrl && <img src={logoUrl} alt="logo" style={{ maxHeight: 60, marginBottom: 4 }} />}
            <div style={{ fontSize: 20, fontWeight: 700 }}>{hospitalName}</div>
            <div style={{ fontSize: 12 }}>{hospitalAddr}</div>
            {hospitalPhone && <div style={{ fontSize: 12 }}>Ph: {hospitalPhone}</div>}
            <hr style={{ borderColor: '#1565c0', margin: '6px 0' }} />
          </div>
        )}

        <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 16, letterSpacing: 2, marginBottom: 8,
          borderBottom: '2px solid #1565c0', paddingBottom: 4, color: '#1565c0' }}>
          HOSPITAL BILL — IPD
        </div>

        {/* Patient Info */}
        <table className="pb-table" style={{ marginBottom: 8 }}>
          <tbody>
            <tr>
              <td className="pb-label">Patient Name</td>
              <td>{patient ? `${patient.firstName} ${patient.lastName}` : '—'}</td>
              <td className="pb-label">Age / Sex</td>
              <td>{patientAge ? `${patientAge} yrs / ${patient?.gender || '—'}` : '—'}</td>
            </tr>
            <tr>
              <td className="pb-label">Ward / Bed</td>
              <td>{admission?.ward?.name || '—'} / {admission?.bed?.bedNumber || '—'}</td>
              <td className="pb-label">Admission #</td>
              <td>{admissionId}</td>
            </tr>
            <tr>
              <td className="pb-label">Admission Date</td>
              <td>{fmtDate(admission?.admissionDate)}</td>
              <td className="pb-label">Discharge Date</td>
              <td>{fmtDate(admission?.actualDischargeDate)}</td>
            </tr>
            <tr>
              <td className="pb-label">Stay Duration</td>
              <td>{days} day{days > 1 ? 's' : ''}</td>
              <td className="pb-label">Bill #</td>
              <td>{billId || '—'}</td>
            </tr>
            <tr>
              <td className="pb-label">Bill Date</td>
              <td>{fmtDate(new Date())}</td>
              <td className="pb-label">Payment Mode</td>
              <td>{paymentMode}</td>
            </tr>
          </tbody>
        </table>

        {/* Line Items */}
        <table className="pb-table" style={{ marginBottom: 8 }}>
          <thead>
            <tr style={{ backgroundColor: '#e3f2fd', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
              <th style={{ textAlign: 'left', color: '#1565c0' }}>#</th>
              <th style={{ textAlign: 'left', color: '#1565c0' }}>Service / Item</th>
              <th style={{ textAlign: 'right', color: '#1565c0' }}>Qty</th>
              <th style={{ textAlign: 'right', color: '#1565c0' }}>Rate (₹)</th>
              <th style={{ textAlign: 'right', color: '#1565c0' }}>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r._id}>
                <td>{i + 1}</td>
                <td>{r.service || '—'}</td>
                <td style={{ textAlign: 'right' }}>{r.qty}</td>
                <td style={{ textAlign: 'right' }}>{fmt(parseFloat(r.rate) || 0)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(r.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="pb-total-row">
              <td colSpan={4} style={{ textAlign: 'right' }}>Subtotal</td>
              <td style={{ textAlign: 'right' }}>₹{fmt(subtotal)}</td>
            </tr>
            {discountAmt > 0 && (
              <tr>
                <td colSpan={4} style={{ textAlign: 'right' }}>Discount ({discount}%)</td>
                <td style={{ textAlign: 'right' }}>- ₹{fmt(discountAmt)}</td>
              </tr>
            )}
            {cgstAmt > 0 && (
              <tr>
                <td colSpan={4} style={{ textAlign: 'right' }}>CGST ({cgst}%)</td>
                <td style={{ textAlign: 'right' }}>₹{fmt(cgstAmt)}</td>
              </tr>
            )}
            {sgstAmt > 0 && (
              <tr>
                <td colSpan={4} style={{ textAlign: 'right' }}>SGST ({sgst}%)</td>
                <td style={{ textAlign: 'right' }}>₹{fmt(sgstAmt)}</td>
              </tr>
            )}
            <tr className="pb-net-row">
              <td colSpan={4} style={{ textAlign: 'right', color: '#fff' }}>NET TOTAL</td>
              <td style={{ textAlign: 'right', color: '#fff' }}>₹{fmt(netAmount)}</td>
            </tr>
          </tfoot>
        </table>

        {/* Footer */}
        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 11, color: '#555' }}>Prepared by: {user ? `${user.firstName} ${user.lastName}` : '—'}</div>
            <div style={{ fontSize: 11, color: '#555' }}>Date: {fmtDate(new Date())}</div>
          </div>
          <div style={{ textAlign: 'center', minWidth: 180 }}>
            <div style={{ borderTop: '1px solid #000', paddingTop: 4 }}>
              Authorised Signatory
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default IpdBillingPage;
